import { Link, useSearch } from "@tanstack/react-router";
import { ArrowLeft, CircleAlert, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { confirmPasswordReset } from "@/lib/auth/auth-api";
import type { ApiError } from "@/lib/api/client";

import { AuthScreen } from "./auth-screen";
import { AuthField, PasswordInput } from "./auth-fields";

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const { uid = "", token = "" } = useSearch({ strict: false }) as {
    uid?: string;
    token?: string;
  };

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [status, setStatus] = useState<"idle" | "saving" | "done">("idle");
  const [error, setError] = useState<string | null>(null);

  const missingLink = !uid || !token;
  const mismatch = pw2.length > 0 && pw1 !== pw2;
  const canSubmit = pw1.length > 0 && pw1 === pw2 && !missingLink;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("saving");
    setError(null);
    try {
      await confirmPasswordReset({
        uid,
        token,
        new_password1: pw1,
        new_password2: pw2,
      });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      const detail = (err as ApiError)?.detail;
      setError(typeof detail === "string" ? detail : t("auth.reset.confirmError"));
    }
  }

  if (status === "done") {
    return (
      <AuthScreen>
        <div className="text-center">
          <h1 className="text-2xl font-extrabold">{t("auth.reset.doneTitle")}</h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("auth.reset.doneBody")}
          </p>
          <Button asChild className="mt-5 w-full">
            <Link to="/login">{t("auth.reset.goToLogin")}</Link>
          </Button>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <h1 className="text-2xl font-extrabold">{t("auth.reset.newTitle")}</h1>
      <p className="mt-1.5 text-[14.5px] text-muted-foreground">
        {t("auth.reset.newBody")}
      </p>

      {missingLink ? (
        <div className="mt-5 space-y-4">
          <p className="flex items-center gap-2 rounded-lg bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive">
            <CircleAlert className="size-4 shrink-0" aria-hidden />
            {t("auth.reset.invalidLink")}
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-primary hover:underline"
          >
            <ArrowLeft className="size-4" aria-hidden />
            {t("auth.reset.requestNew")}
          </Link>
        </div>
      ) : (
        <form noValidate onSubmit={submit}>
          <AuthField label={t("auth.reset.newPassword")} htmlFor="reset-pw1">
            <PasswordInput
              id="reset-pw1"
              autoComplete="new-password"
              value={pw1}
              onChange={setPw1}
            />
          </AuthField>
          <AuthField
            label={t("auth.reset.confirmPassword")}
            htmlFor="reset-pw2"
            error={mismatch ? t("auth.reset.mismatch") : undefined}
          >
            <PasswordInput
              id="reset-pw2"
              autoComplete="new-password"
              value={pw2}
              onChange={setPw2}
            />
          </AuthField>

          {error && (
            <p role="alert" className="mt-4 text-sm font-medium text-destructive">
              {error}
            </p>
          )}

          <Button
            type="submit"
            className="mt-5 w-full"
            disabled={!canSubmit || status === "saving"}
          >
            {status === "saving" && <Loader2 className="size-4 animate-spin" aria-hidden />}
            {t("auth.reset.submit")}
          </Button>
        </form>
      )}
    </AuthScreen>
  );
}
